import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { GraduationCap } from '@phosphor-icons/react';
import { Link, useNavigate } from 'react-router-dom';

export default function JoinClassroomPage() {
    const { api } = useAuth();
    const navigate = useNavigate();
    const [classCode, setClassCode] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const handleJoin = async (e) => {
        e.preventDefault();
        if (!classCode.trim()) { toast.error('Enter a class code'); return; }
        setSubmitting(true);
        try {
            const res = await api().post('/educator/join-classroom', { class_code: classCode.trim().toUpperCase() });
            setResult(res.data);
            toast.success(res.data.message);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Invalid class code');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md"
            >
                <div className="brutalist-card p-6 md:p-8">
                    <div className="text-center mb-6">
                        <div className="w-16 h-16 mx-auto rounded-full bg-[#22c55e] border-4 border-[#1e293b] flex items-center justify-center mb-4">
                            <GraduationCap weight="fill" className="text-white" size={32} />
                        </div>
                        <h1 className="font-fredoka text-2xl md:text-3xl font-bold text-[#1e293b]">
                            Join a Classroom
                        </h1>
                        <p className="text-[#475569] mt-2">
                            Enter the class code your teacher gave you
                        </p>
                    </div>

                    {result ? (
                        <div className="text-center">
                            <div className="bg-[#22c55e]/10 border-2 border-[#22c55e] rounded-xl p-4 mb-4">
                                <p className="font-fredoka font-bold text-[#1e293b] text-lg">{result.message}</p>
                                {result.classroom_name && (
                                    <p className="text-[#475569] mt-1">Classroom: {result.classroom_name}</p>
                                )}
                            </div>
                            <Link to="/dashboard" className="brutalist-btn bg-[#22c55e] text-white inline-block" data-testid="go-to-dashboard">
                                Go to Dashboard
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={handleJoin} className="space-y-5">
                            <div>
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">Class Code</label>
                                <input
                                    type="text"
                                    value={classCode}
                                    onChange={(e) => setClassCode(e.target.value.toUpperCase())}
                                    className="brutalist-input w-full text-center text-2xl font-mono tracking-wider"
                                    placeholder="BR-XXXX"
                                    maxLength={10}
                                    data-testid="class-code-input"
                                />
                            </div>
                            <motion.button
                                type="submit"
                                disabled={submitting}
                                whileHover={{ y: -2, x: -2 }}
                                whileTap={{ y: 2, x: 2 }}
                                className="brutalist-btn w-full bg-[#22c55e] text-white text-lg disabled:opacity-50"
                                data-testid="join-classroom-submit"
                            >
                                {submitting ? 'Joining...' : 'Join Classroom'}
                            </motion.button>
                            <Link to="/dashboard" className="block text-center text-[#475569] hover:text-[#1e293b] font-semibold">
                                Back to Dashboard
                            </Link>
                        </form>
                    )}
                </div>
            </motion.div>
        </div>
    );
}
